import { z } from 'zod';

/**
 * Single source of truth for every `import.meta.env` read the web app
 * makes. Zod parses once at module load and caches the result, so a
 * missing or malformed value surfaces at boot rather than the first
 * request that happens to touch it.
 *
 * `VITE_API_ORIGIN` is required: an empty string would silently make
 * fetches relative to the SPA origin (i.e. talk to the SPA itself).
 * `VITE_SENTRY_*` are optional — the Sentry init is a no-op when DSN is
 * absent.
 */

const EnvSchema = z.object({
  apiOrigin: z.url(),
  sentry: z.object({
    dsn: z.string().min(1).optional(),
    release: z.string().min(1).optional(),
    // 0..1, parsed downstream by the Sentry helper.
    tracesSampleRate: z.string().min(1).optional(),
    // When true, session replays capture text/inputs/media unredacted.
    // For testing only — never enable in production.
    replayNoRedact: z.boolean(),
  }),
  googleMapsApiKey: z.string().min(1).optional(),
  /**
   * Local dev only. Google refuses a `.local` redirect URI outright, so the dev
   * OAuth client registers the loopback `http://localhost:<port>/…` callback
   * instead. When the browser lands there, the callback pages forward it to
   * this origin (the `.local` app) so the code exchange runs same-origin — with
   * the session cookie and the `sessionStorage` connect stash, neither of which
   * crosses origins. Unset in every real deployment.
   */
  oauthLocalForwardOrigin: z.url().optional(),
  /**
   * The Chrome id of the immoOS browser extension (IMM-1381) „Erweiterung verbinden" hands its key to. Unset, it is
   * the id the key in `apps/extension-chrome/manifest.json` fixes; a Web Store build sets its own.
   */
  browserExtensionId: z
    .string()
    .regex(/^[a-p]{32}$/)
    .optional(),
  /**
   * Set (`VITE_SANDBOX=true`) only by the hosted prod-data sandbox build, which
   * shows a persistent banner. Anything but the literal `true` is off.
   */
  sandbox: z.boolean(),
  mode: z.string().min(1),
  isDev: z.boolean(),
});

export type Env = z.infer<typeof EnvSchema>;

function readRawValue(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;

  return value.length > 0 ? value : undefined;
}

function parse(): Env {
  try {
    return EnvSchema.parse({
      apiOrigin: readRawValue(import.meta.env['VITE_API_ORIGIN']),
      sentry: {
        dsn: readRawValue(import.meta.env['VITE_SENTRY_DSN']),
        release: readRawValue(import.meta.env['VITE_SENTRY_RELEASE']),
        tracesSampleRate: readRawValue(import.meta.env['VITE_SENTRY_TRACE_RATE']),
        replayNoRedact: import.meta.env['VITE_SENTRY_REPLAY_NO_REDACT'] === 'true',
      },
      googleMapsApiKey: readRawValue(import.meta.env['VITE_GOOGLE_MAPS_API_KEY']),
      oauthLocalForwardOrigin: readRawValue(import.meta.env['VITE_OAUTH_LOCAL_FORWARD_ORIGIN']),
      browserExtensionId: readRawValue(import.meta.env['VITE_BROWSER_EXTENSION_ID']),
      sandbox: import.meta.env['VITE_SANDBOX'] === 'true',
      mode: import.meta.env.MODE,
      isDev: import.meta.env.DEV,
    });
  } catch (err) {
    if (err instanceof z.ZodError) {
      const summary = err.issues.map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`).join('\n');
      // Fail loudly. Without this the SPA boots with broken
      // configuration and the failure shows up much later in a
      // confusing place.
      throw new Error(`Invalid web-app environment configuration:\n${summary}`);
    }

    throw err;
  }
}

export const env: Env = parse();
